import { type Collection, List as ImmutableList, type Map as ImmutableMap } from 'immutable';
import { createSelector } from 'reselect';

import { accountsUtils } from 'app-components/flux/accounts';
import type { AccountProps } from 'app-components/flux/accounts/accountsTypes';
import type RootState from 'app-components/utils/redux-store/rootState';

import { getCurrentDateString } from '../date/dateSelectors';
import type { DATE_REDUCER_KEY } from '../date/dateTypes';
import {
  ACCOUNTS_BALANCES_REDUCER_KEY,
  type AccountsBalance,
  type AccountsBalancesStore,
} from './accountsBalancesTypes';

export type AccountsBalancesSelectorsState = Pick<RootState, typeof ACCOUNTS_BALANCES_REDUCER_KEY>;
export type AccountsBalancesWithDateState = AccountsBalancesSelectorsState & Pick<RootState, typeof DATE_REDUCER_KEY>;

type BalanceAccount = AccountProps | null | undefined;
export type BalanceAccountsCollection = readonly BalanceAccount[] | Collection<unknown, BalanceAccount>;

export type SimplifiedBalance = {
  balanceAmount: number | null | undefined;
  balanceOn: string;
  fakeData?: boolean;
};

export type SimplifiedAccountBalances = {
  name: AccountProps['name'];
  id: AccountProps['id'];
  type: AccountProps['type'];
  accountBalances: SimplifiedBalance[];
  colorIndex: number;
};

export const getResourceStore = (state: AccountsBalancesSelectorsState): AccountsBalancesStore =>
  state[ACCOUNTS_BALANCES_REDUCER_KEY];

export const getLoadPending = (state: AccountsBalancesSelectorsState): boolean => getResourceStore(state).loadPending;
export const getIsLoading = (state: AccountsBalancesSelectorsState): boolean => getResourceStore(state).isLoading;
export const getError = (state: AccountsBalancesSelectorsState): unknown => getResourceStore(state).error;
export const getResourcesById = (state: AccountsBalancesSelectorsState): ImmutableMap<string, AccountsBalance> =>
  getResourceStore(state).resourcesById;

export const getLastSyncDate = (state: AccountsBalancesSelectorsState): string | null | undefined =>
  getResourceStore(state).lastSyncDate;

// Filter by balanceType === 'CURRENT' if any 'CURRENT' balanceTypes exist (This is going to be the more accurate value)
const filterAndSortBalancesForAccount = (
  account: AccountProps,
  accountBalances: ImmutableMap<string, AccountsBalance>,
): AccountsBalance[] => {
  let alteredAccountBalances = accountBalances;

  alteredAccountBalances = alteredAccountBalances.filter((balance) => balance.accountId === account.id);

  const prioritizedBalanceType =
    accountsUtils.isInvestmentAccount(account) && !accountsUtils.isAccountManual(account) ? 'ONLINE' : 'CURRENT';

  // Reference MAP that contains all balances. If there are duplicate balances, it only contains the PRIORITIZED type for that date.
  const balanceTypesForDates = new Map<AccountsBalance['balanceOn'], AccountsBalance['balanceType']>();

  alteredAccountBalances.forEach((bal) => {
    if (!balanceTypesForDates.has(bal.balanceOn)) {
      // If the date is not included in the list, add it
      balanceTypesForDates.set(bal.balanceOn, bal.balanceType);
    } else if (balanceTypesForDates.get(bal.balanceOn) !== prioritizedBalanceType) {
      // If the date IS included in the list, and it is NOT the prioritized type, replace it
      balanceTypesForDates.set(bal.balanceOn, bal.balanceType);
    }
  });
  alteredAccountBalances = alteredAccountBalances.filter((bal) => {
    //  Include this balance if its balanceType equals the assigned balance type for the date in uniqueBalances
    return bal.balanceType === balanceTypesForDates.get(bal.balanceOn);
  });

  //  Sort the balances by date
  return alteredAccountBalances
    .valueSeq()
    .toArray()
    .sort((a, b) => {
      if (a.balanceOn == null || b.balanceOn == null) {
        return 0;
      } else if (a.balanceOn > b.balanceOn) {
        return 1;
      } else if (b.balanceOn > a.balanceOn) {
        return -1;
      } else {
        return 0;
      }
    });
};

// This is _NOT_ going to always be accurate. But it is dirt cheap compared to parsing dates
// This should only be used as a performance short-circuit because it will never create false positives
const dirtyAddOneDay = (isoDate: string): string => {
  const yearDashMonthDash = isoDate.slice(0, 8);
  const day = Number.parseInt(isoDate.slice(8, 10), 10);
  const newDay = day < 9 ? '0' + (day + 1) : day + 1;
  return `${yearDashMonthDash}${newDay}`;
};

// JS dates, not luxon
const jsDatesAreOnSameDay = (first: Date, second: Date): boolean =>
  first.getDate() === second.getDate() &&
  first.getMonth() === second.getMonth() &&
  first.getFullYear() === second.getFullYear();

function jsAddDays(date: Date, days: number): void {
  date.setDate(date.getDate() + days);
}

function sameMonth(first: string, second: string): boolean {
  return second.startsWith(first.slice(0, 7));
}

function fillDateRange(
  adjustedAccountBalances: SimplifiedBalance[],
  startISO: string,
  endISO: string,
  amount: number | null | undefined,
): void {
  // If we aren't crossing any month boundaries we can do some dirty string hacks to avoid creating/mutating Date objects
  if (sameMonth(startISO, endISO)) {
    let firstDate = startISO;
    const secondDate = endISO;
    // Since the month is the same we can cheat and use the dirty add day method
    firstDate = dirtyAddOneDay(firstDate);
    if (firstDate !== secondDate) {
      // We need to add the missing dates
      while (firstDate < secondDate) {
        adjustedAccountBalances.push({
          balanceAmount: amount,
          balanceOn: firstDate,
          fakeData: true,
        });
        firstDate = dirtyAddOneDay(firstDate);
      }
    }
  } else {
    const firstDate = new Date(startISO);
    const secondDate = new Date(endISO);
    jsAddDays(firstDate, 1);
    //  And there ARE missing dates in between
    if (!jsDatesAreOnSameDay(firstDate, secondDate)) {
      while (firstDate < secondDate) {
        adjustedAccountBalances.push({
          balanceAmount: amount,
          balanceOn: firstDate.toISOString().slice(0, 10),
          fakeData: true,
        });
        jsAddDays(firstDate, 1);
      }
    }
  }
}

const colorIndexMaxValue = 10;

//  Returns a list of account objects that include the account name, id, and list of balances connected to that account sorted by date
//  In addition, since QCS only updates changes to an account on specific dates, it fills in the blank dates with the
//  previous dates' value
export const getSimplifiedBalancesByAccount = createSelector(
  (_state: AccountsBalancesWithDateState, accounts?: BalanceAccountsCollection | null) => accounts,
  getResourcesById,
  getCurrentDateString,
  function balancesByAccountInternal(accounts, balances, todayISO): ImmutableList<SimplifiedAccountBalances> {
    const listOfAccountsByBalances: SimplifiedAccountBalances[] = [];

    let index = 0;
    accounts?.forEach((account) => {
      if (!account) {
        return;
      }

      //  Only include the balances for the selected account
      const accountBalances = balances?.size ? filterAndSortBalancesForAccount(account, balances) : [];

      const adjustedAccountBalances: SimplifiedBalance[] = [];

      //  This handles filling in missing dates with the values of the previous date!
      for (let i = 0; i < accountBalances?.length; i++) {
        const balanceOn = accountBalances[i].balanceOn;
        if (balanceOn == null) {
          throw new TypeError('An account balance must have a balanceOn date to fill its date range');
        }
        adjustedAccountBalances.push({
          balanceAmount: accountBalances[i].balanceAmount,
          balanceOn,
        });
        fillDateRange(
          adjustedAccountBalances,
          balanceOn,
          accountBalances[i + 1]?.balanceOn ?? todayISO,
          accountBalances[i].balanceAmount,
        );
      }
      // We need to make sure to add today on the end if it is not already there
      if (
        adjustedAccountBalances.length > 0 &&
        adjustedAccountBalances[adjustedAccountBalances.length - 1].balanceOn !== todayISO
      ) {
        adjustedAccountBalances.push({
          balanceAmount: accountBalances[accountBalances.length - 1].balanceAmount,
          balanceOn: todayISO,
        });
      }
      //  Push the balances to the account object!
      listOfAccountsByBalances.push({
        name: account.name,
        id: account.id,
        type: account.type,
        accountBalances: adjustedAccountBalances,
        colorIndex: index % colorIndexMaxValue,
      });
      index += 1;
    });

    //  Sort the list by longest held account.
    const sortedList = listOfAccountsByBalances.sort((a, b) => b.accountBalances.length - a.accountBalances.length);
    return ImmutableList(sortedList);
  },
);

export const getOldestAvailableBalanceDate = createSelector(getResourcesById, (balancesById): string | null => {
  if (!balancesById || balancesById.size === 0) {
    return null;
  }

  return balancesById.reduce<string | null>((oldest, bal) => {
    const date = bal?.balanceOn;
    if (!date) {
      return oldest;
    }
    return oldest === null || date < oldest ? date : oldest;
  }, null);
});
